/**
 * CSV Parser Service
 *
 * Provides functions to parse and validate CSV files for bulk import.
 * Uses papaparse library for robust CSV parsing with error handling.
 *
 * @module ImportExportTab/services/csvParser
 */

import Papa from 'papaparse';
import { MAX_FILE_SIZE } from '../constants';

/**
 * Parse CSV file and return structured data
 *
 * Validates file type and size before parsing. Handles UTF-8 BOM encoding,
 * trims whitespace, and skips empty rows. Returns parsed data with error
 * information and metadata for validation.
 *
 * @param {File} file - File object from file input or drag-and-drop
 * @param {Object} [options={}] - Optional parsing configuration
 * @param {number} [options.maxSize] - Maximum file size in bytes (default: 5MB)
 * @returns {Promise<Object>} Parse result object
 * @returns {Array<Object>} result.data - Parsed rows as objects with trimmed values
 * @returns {Array<Object>} result.errors - Parse errors with row/column info
 * @returns {Object} result.meta - Metadata including field names
 * @returns {string} result.filename - Original filename
 * @throws {Error} If file is invalid (wrong type, too large, etc.)
 *
 * @example
 * const file = event.target.files[0];
 * const result = await parseCSVFile(file);
 * if (result.errors.length === 0) {
 *   console.log('Parsed data:', result.data);
 * }
 */
export async function parseCSVFile(file, options = {}) {
  const maxSize = options.maxSize || MAX_FILE_SIZE;

  // Validate file type by extension
  if (!file.name.toLowerCase().endsWith('.csv')) {
    throw new Error('Invalid file type. Please upload a CSV file (.csv extension required).');
  }

  // Validate file size
  if (file.size > maxSize) {
    const sizeMB = (maxSize / (1024 * 1024)).toFixed(0);
    throw new Error(`File size exceeds maximum limit of ${sizeMB}MB.`);
  }

  // Parse file using papaparse
  return new Promise((resolve, reject) => {
    Papa.parse(file, {
      header: true,              // First row is header
      skipEmptyLines: true,      // Skip empty rows
      trimHeaders: true,         // Trim whitespace from headers
      transformHeader: (header) => {
        // Remove UTF-8 BOM if present
        return header.replace(/^\uFEFF/, '').trim();
      },
      transform: (value) => {
        // Trim whitespace from all values
        return typeof value === 'string' ? value.trim() : value;
      },
      complete: (results) => {
        resolve({
          data: results.data || [],
          errors: results.errors || [],
          meta: results.meta || {},
          filename: file.name
        });
      },
      error: (error) => {
        reject(new Error(`Failed to parse CSV: ${error.message}`));
      }
    });
  });
}

/**
 * Validate CSV structure matches expected template
 *
 * Checks that all required headers are present in the CSV file.
 * Does not validate data values - only structure.
 *
 * @param {Object} parseResult - Result from parseCSVFile()
 * @param {string[]} requiredHeaders - Array of required header names
 * @returns {Object} Validation result
 * @returns {boolean} result.valid - True if structure is valid
 * @returns {string[]} result.missingHeaders - Array of missing required headers
 * @returns {string[]} result.extraHeaders - Array of headers not in template
 *
 * @example
 * const result = await parseCSVFile(file);
 * const validation = validateCSVStructure(result, ['name', 'orgLevel']);
 * if (!validation.valid) {
 *   console.error('Missing headers:', validation.missingHeaders);
 * }
 */
export function validateCSVStructure(parseResult, requiredHeaders) {
  const actualHeaders = parseResult.meta.fields || [];
  const missingHeaders = requiredHeaders.filter(h => !actualHeaders.includes(h));
  const extraHeaders = actualHeaders.filter(h => !requiredHeaders.includes(h));

  return {
    valid: missingHeaders.length === 0,
    missingHeaders,
    extraHeaders,
    actualHeaders
  };
}
